import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Stack,
  InputAdornment,
  IconButton,
  Divider,
  Link,
  CircularProgress,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import KeyIcon from '@mui/icons-material/Key';
import KeyboardCapslockIcon from '@mui/icons-material/KeyboardCapslock';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Tooltip from '@mui/material/Tooltip';
import { useAuth } from '@/hooks/useAuth';
import { usePreferences } from '@/contexts/PreferencesContext';
import { authService } from '@/services/authService';

export const LoginPage = () => {
  const { login, adminPortalUrl } = useAuth();
  const { preferences } = usePreferences();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [resetOtp, setResetOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [view, setView] = useState<'login' | 'forgot' | 'reset'>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showOtp, setShowOtp] = useState(false);
  const [capsOn, setCapsOn] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const trackCaps = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (typeof e.getModifierState === 'function') setCapsOn(e.getModifierState('CapsLock'));
  };

  const switchView = (next: 'login' | 'forgot' | 'reset') => {
    setView(next);
    setError(null);
    setInfo(null);
    if (next !== 'login') {
      setShowOtp(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setLoading(true);

    try {
      await login(email, password, otp || undefined);
    } catch (err: any) {
      const message = err.message || 'Login failed';
      // Check if OTP is required
      const normalized = message.toLowerCase();
      if (normalized.includes('reset') || normalized.includes('expired')) {
        setView('forgot');
        setShowOtp(false);
        setError(message);
      } else if (normalized.includes('otp') || normalized.includes('2fa') || normalized.includes('verification')) {
        setShowOtp(true);
        setError('Please enter your verification code');
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setError(null);
    setInfo(null);
    setLoading(true);

    try {
      const res = await authService.requestPasswordReset(email);
      switchView('reset');
      setResetToken(res.resetToken || '');
      setInfo(res.note || 'Reset token sent.');
    } catch (err: any) {
      setError(err.message || 'Failed to request reset token');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !resetToken || !resetOtp || !newPassword) return;
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setError(null);
    setInfo(null);
    setLoading(true);

    try {
      await authService.resetPassword({
        email,
        resetToken,
        otp: resetOtp,
        newPassword
      });
      setNewPassword('');
      setConfirmPassword('');
      setResetOtp('');
      setResetToken('');
      switchView('login');
      setInfo('Password updated. Please sign in.');
    } catch (err: any) {
      setError(err.message || 'Password reset failed');
    } finally {
      setLoading(false);
    }
  };

  const productName = preferences.branding?.productName || 'Webmail';
  const logoUrl = preferences.branding?.logoDataUrl;
  const headerTitle = view === 'login'
    ? `Sign in to ${productName}`
    : view === 'forgot'
      ? 'Reset your password'
      : 'Set a new password';
  const headerSubtitle = view === 'login'
    ? 'Enter your credentials to access your mailbox'
    : view === 'forgot'
      ? 'We will send you a reset token if your email is found'
      : 'Enter the reset token and a new password to continue';
  const errorLower = error ? error.toLowerCase() : '';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Card
        elevation={8}
        sx={{
          width: '100%',
          maxWidth: 420,
          borderRadius: 3,
        }}
      >
        <CardContent sx={{ p: 4 }}>
          {/* Logo/Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            {logoUrl ? (
              <Box
                component="img"
                src={logoUrl}
                alt={productName}
                sx={{ maxHeight: 48, maxWidth: '100%', mb: 2 }}
              />
            ) : (
              <EmailIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
            )}
            <Typography variant="h5" fontWeight={600}>
              {headerTitle}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {headerSubtitle}
            </Typography>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert
              severity={view === 'login' && showOtp && errorLower.includes('verification') ? 'info' : 'error'}
              sx={{ mb: 3 }}
            >
              {error}
            </Alert>
          )}

          {info && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {info}
            </Alert>
          )}

          {view === 'login' && (
            <>
              {/* Login Form */}
              <form onSubmit={handleSubmit}>
                <Stack spacing={2.5}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    required
                    autoComplete="email"
                    autoFocus
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={() => setPasswordFocused(true)}
                    onBlur={() => { setPasswordFocused(false); setCapsOn(false); }}
                    onKeyDown={trackCaps}
                    onKeyUp={trackCaps}
                    disabled={loading}
                    required
                    autoComplete="current-password"
                    helperText={passwordFocused && capsOn ? 'Caps Lock is on' : undefined}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          {passwordFocused && capsOn && (
                            <Tooltip title="Caps Lock is on">
                              <KeyboardCapslockIcon fontSize="small" color="warning" sx={{ mr: 0.5 }} />
                            </Tooltip>
                          )}
                          <IconButton
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            size="small"
                            tabIndex={-1}
                          >
                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  {showOtp && (
                    <TextField
                      fullWidth
                      label="Verification Code"
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      disabled={loading}
                      autoComplete="one-time-code"
                      placeholder="Enter 6-digit code"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <KeyIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={loading || !email || !password}
                    sx={{ py: 1.5, mt: 1 }}
                  >
                    {loading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      'Sign In'
                    )}
                  </Button>
                </Stack>
              </form>

              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  onClick={() => switchView('forgot')}
                  sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                >
                  Forgot your password?
                </Link>
              </Box>

              {/* Divider */}
              <Divider sx={{ my: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  or
                </Typography>
              </Divider>

              {/* Admin Portal Link */}
              <Box sx={{ textAlign: 'center' }}>
                <Link
                  href={adminPortalUrl}
                  underline="hover"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: 'text.secondary',
                    '&:hover': { color: 'primary.main' },
                  }}
                >
                  <ArrowBackIcon fontSize="small" />
                  Go to Admin Portal
                </Link>
              </Box>
            </>
          )}

          {view === 'forgot' && (
            <>
              <form onSubmit={handleForgot}>
                <Stack spacing={2.5}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    required
                    autoComplete="email"
                    autoFocus
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={loading || !email}
                    sx={{ py: 1.5, mt: 1 }}
                  >
                    {loading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      'Send Reset Token'
                    )}
                  </Button>
                </Stack>
              </form>

              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  onClick={() => switchView('login')}
                  sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                >
                  Back to sign in
                </Link>
              </Box>
            </>
          )}

          {view === 'reset' && (
            <>
              <form onSubmit={handleReset}>
                <Stack spacing={2.5}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    required
                    autoComplete="email"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Reset Token"
                    type="text"
                    value={resetToken}
                    onChange={(e) => setResetToken(e.target.value)}
                    disabled={loading}
                    required
                    autoFocus
                    helperText={resetToken ? 'Use the token sent to your email.' : undefined}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <KeyIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Verification Code"
                    type="text"
                    value={resetOtp}
                    onChange={(e) => setResetOtp(e.target.value)}
                    disabled={loading}
                    required
                    autoComplete="one-time-code"
                    placeholder="Enter verification code"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <KeyIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    fullWidth
                    label="New Password"
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    disabled={loading}
                    required
                    autoComplete="new-password"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            size="small"
                          >
                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Confirm Password"
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={loading}
                    required
                    autoComplete="new-password"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            size="small"
                          >
                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={loading || !email || !resetToken || !resetOtp || !newPassword || !confirmPassword}
                    sx={{ py: 1.5, mt: 1 }}
                  >
                    {loading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      'Update Password'
                    )}
                  </Button>
                </Stack>
              </form>

              <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 2 }}>
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  onClick={() => switchView('login')}
                  sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                >
                  Back to sign in
                </Link>
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  onClick={() => switchView('forgot')}
                  sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                >
                  Request new token
                </Link>
              </Stack>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default LoginPage;
